import React, {
    forwardRef,
    ReactNode,
    useEffect,
    useImperativeHandle,
    useMemo,
    useReducer,
    useRef
} from 'react';
import { Message, Upload as BaseUpload, UploadFile, UploadProps } from '@fx-ui/fine-design';
import {
    assign,
    concat,
    each,
    filter,
    find,
    forEach,
    get,
    includes,
    indexOf,
    isEmpty,
    isEqual,
    isFireFoxUA,
    isFunction,
    isNil,
    isNumber,
    isString,
    map,
    parseFileName,
    post,
    set,
    size,
    some,
    sortBy,
    split
} from '~/utils';
import Intl from '@fx-ui/intl';
import { IFile, NewUploadFilesValue, UploadProgress } from '~/interfaces';
import { ReactSortable } from '~/components';
import classNames from 'classnames';
import { UPLOAD_FILE_COUNT } from '@libs/shared';

export interface UploadRef {
    click: () => void;
    upload: (file: IFile) => void;
    dropUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
    remove: (value: string, key: string) => void;
    removeFile: (file: UploadFile) => void;
    setActionUrl: (url: string) => void;
}

export interface IUploadProps extends UploadProps {
    value?: IFile[];
    editable?: boolean;
    uploadFailTip?: string;
    text?: string;
    resultFileKey?: string;
    uploadTokenUrl?: string; // 获取token的url
    uploadPolicyUrl?: string; // 获取oss-policy的url
    minFileSize?: number | null; // 单个文件最小size
    minSizeTip?: string | ((sizeMB: number) => React.ReactNode); // 文件大小不足提示
    maxFileSize?: number | null; // 单个文件最大size
    getMaxFileSize?: (upFiles: FileList) => number; // 动态定义单个文件最大size
    maxSizeTip?: string | ((sizeMB: number) => React.ReactNode); // 文件大小超限提示
    maxFileCount?: number | null; // 单次最大文件上传量
    unSupportFileTypeTip?: string | ((file: FileList) => React.ReactNode); // 文件类型限制提示
    maxCountTip?: string | ((fileCount: number) => React.ReactNode); // 文件数量超限提示
    onGetUploadTokenData?: (fileList: FileList) => object;
    onGetUploadPolicyData?: (fileList: FileList) => object;
    onGetUploadTokenResult?: (fileList: FileList) => object;
    onGetUploadPolicyResult?: (fileList: FileList) => object;
    onGetUploadTokenError?: (error: object) => void;
    onGetUploadPolicyError?: (error: object) => void;
    onBeforeUpload?: (fileList: FileList) => any;
    onChange?: (value: IFile[]) => void;
    onFileChange?: (files: any[]) => void; // 本地文件变更
    onStateChange?: (isUploading: boolean) => void;
    // render-function
    defaultFileRender?: (file: IFile, remove: (file: IFile) => void) => React.ReactNode;
    newUploadFileRender?: (
        file: NewUploadFilesValue,
        remove: (file: File) => void
    ) => React.ReactNode;
    emptyItemCount?: number;
    allowSort?: boolean;
    children?: ReactNode;
}

type NewUploadFilesReducerActionChange = Partial<Omit<NewUploadFilesValue, 'upFile'>>;

type FilesReducerValue = IFile | NewUploadFilesValue;

type FilesReducerAction =
    | { type: 'add'; payload: { files: FilesReducerValue[] } }
    | { type: 'reset'; payload: { files: FilesReducerValue[] } }
    | { type: 'remove'; payload: { files: string[] } }
    | { type: 'removeFile'; payload: { files: File[] } }
    | { type: 'removeUpload'; payload: { files: string[]; fileKey?: string } }
    | { type: 'change'; payload: { files: File[]; value: NewUploadFilesReducerActionChange } }
    | { type: 'replace'; payload: { files: File[]; value: IFile } };

const isUploadFile = (f: any): f is NewUploadFilesValue => {
    return f.upFile instanceof File;
};

const filesReducer = (state: FilesReducerValue[], action: FilesReducerAction) => {
    const { type, payload } = action;
    switch (type) {
        case 'add': // 向文件列表追加文件
            return concat(state, payload.files as FilesReducerValue[]);
        case 'reset': // 重置文件列表
            return payload.files;
        case 'remove': // 移除某些文件（不区分本地文件和上传文件）
            return filter(state, ({ _id }) => !includes(payload.files as string[], _id));
        case 'removeFile': // 移除某些本地文件（File）
            return filter(state, (file) => {
                if (isUploadFile(file)) {
                    return !includes(payload.files as File[], file.upFile);
                }
                return true;
            });
        case 'removeUpload': {
            // 移除某些上传文件（IFile）
            const fileKey = get(payload, 'fileKey', '_id');
            return filter(state, (file) => {
                if (!isUploadFile(file)) {
                    return !includes(payload.files as string[], get(file, fileKey));
                }
                return true;
            });
        }
        case 'change': // 更新某些文件
            return map(state, (newFile: NewUploadFilesValue) => {
                if (includes(payload.files as UploadFile[], newFile.upFile)) {
                    return {
                        ...newFile,
                        ...(payload as any).value
                    };
                }
                return newFile;
            });
        case 'replace': // 替换某些文件
            return map(state, (newFile: NewUploadFilesValue) => {
                if (includes(payload.files as UploadFile[], newFile.upFile)) {
                    return payload.value;
                }
                return newFile;
            });
        default:
            return state;
    }
};

const getEmptyItems = (emptyItemCount: number) => {
    const items = [];
    for (let i = 0; i < emptyItemCount; i++) {
        items.push({
            _id: `empty${i}`,
            type: 'empty'
        });
    }
    return items;
};

const Upload = forwardRef<UploadRef, IUploadProps>((props, ref) => {
    const {
        className,
        value,
        style,
        accept,
        multiple,
        action,
        headers,
        data,
        editable = true,
        disabled,
        withCredentials,
        maxFileCount,
        maxCountTip,
        minFileSize = 0,
        minSizeTip = Intl.get('upload.min.limit.tip'),
        maxFileSize,
        getMaxFileSize,
        maxSizeTip,
        unSupportFileTypeTip,
        uploadFailTip,
        resultFileKey = 'file',
        onProgress,
        onChange,
        onFileChange,
        onStateChange,
        onSuccess,
        onError,
        uploadTokenUrl,
        uploadPolicyUrl,
        onGetUploadTokenData,
        onGetUploadPolicyData,
        onGetUploadTokenResult,
        onGetUploadPolicyResult,
        onGetUploadTokenError,
        onGetUploadPolicyError,
        onBeforeUpload,
        onDrop,
        onDragEnter,
        onDragOver,
        onDragLeave,
        defaultFileRender,
        newUploadFileRender,
        children,
        enableDrag,
        text,
        emptyItemCount = 0,
        allowSort,
        beforeUpload
    } = props;

    const actionUrl = useRef<string>(isString(action) ? action : '');
    const tokenList = useRef<string[]>([]);

    const uploadRef = useRef<any>(null);
    // 已上传的数据
    const uploadedFields = useRef<IFile[]>(value || []);
    // 上传中的数量
    const uploadingNum = useRef<number>(0);
    // 展示的文件id
    const fileIdsRef = useRef<string[]>([]);
    // 文件列表数据
    const [files, dispatchFiles] = useReducer(filesReducer, []);

    const listItems = useMemo(() => {
        return concat(files, getEmptyItems(emptyItemCount));
    }, [files, emptyItemCount]);

    useImperativeHandle(ref, () => ({
        click: () => {
            if (uploadRef.current) {
                uploadRef.current.handleClick();
            }
        },
        upload: (file: IFile) => {
            if (checkFilesNum([file])) {
                dispatchFiles({ type: 'add', payload: { files: [file] } });
                if (multiple) {
                    uploadedFields.current = concat(uploadedFields.current, file);
                } else {
                    uploadedFields.current = [file];
                }
                fileIdsRef.current = concat(fileIdsRef.current, [file._id]);
                handUploadChange();
            }
        },
        dropUpload: (e: React.ChangeEvent<HTMLInputElement>) => {
            if (uploadRef.current) {
                uploadRef.current.handleChange(e);
            }
        },
        remove: (value: string, key: string) => {
            dispatchFiles({
                type: 'removeUpload',
                payload: { files: [value], fileKey: key }
            });
            uploadedFields.current = filter(
                uploadedFields.current,
                (uploadedField) => value !== get(uploadedField, key)
            );
            handUploadChange();
        },
        removeFile: handleNewUploadRemove, // 抛出删除文件的方法(可以删除上传中文件)
        setActionUrl
    }));

    useEffect(() => {
        const uploadingFiles = filter(files, (file) => isNil(file._id));
        dispatchFiles({
            type: 'reset',
            payload: {
                files: concat(value || [], uploadingFiles)
            }
        });
        // 同步value到内部缓存
        uploadedFields.current = value || [];
    }, [value]);

    useEffect(() => {
        fileIdsRef.current = map(files, (file) => file._id);
        if (onFileChange) {
            onFileChange(files);
        }
    }, [files]);

    /**
     * 校验文件数量
     *
     * @param upFiles
     */
    const checkFilesNum = (upFiles: any[] | FileList) => {
        const curFiles = uploadedFields.current;
        // 文件总数 = 正在上传的 + 已经上传的 + 当前选中的
        const filesTotalNum = uploadingNum.current + size(upFiles) + size(curFiles);
        if (isNumber(maxFileCount) && filesTotalNum > maxFileCount) {
            Message.add({
                messageType: 'toast',
                type: 'warning',
                message: isFunction(maxCountTip) ? maxCountTip(maxFileCount!) : maxCountTip
            });
            return false;
        }
        return true;
    };

    const handUploadChange = () => {
        if (onChange) {
            const fileIds = fileIdsRef.current;
            // 按照展示的顺序对文件排序
            const newFiles = sortBy(uploadedFields.current, (file) => {
                return indexOf(fileIds, file._id);
            });
            onChange(newFiles);
        }
    };

    const handleEndUploadFile = () => {
        uploadingNum.current = uploadingNum.current - 1;
        if (uploadingNum.current <= 0) {
            if (onStateChange) {
                onStateChange(false);
            }
            handUploadChange();
        }
    };

    // 已保存的文件数据移除
    const handleDefaultUploadRemove = (file: IFile) => {
        dispatchFiles({ type: 'removeUpload', payload: { files: [file._id] } });
        uploadedFields.current = filter(uploadedFields.current, ({ _id }) => _id !== file._id);
        handUploadChange();
    };

    const handleNewUploadRemove = (file: UploadFile) => {
        const delFile = find(files, ({ upFile }) => upFile === file);
        if (delFile) {
            dispatchFiles({ type: 'removeFile', payload: { files: [file] } });
            uploadedFields.current = filter(
                uploadedFields.current,
                ({ _id }) => _id !== delFile._id
            );
            // 如果删除的是上传中的文件，需要修改正在上传的文件数量
            if (!delFile.finish) {
                uploadingNum.current -= 1;
            }
        }
        handUploadChange();

        // 删除后中断上传请求，避免浪费流量
        uploadRef.current.doAbort(file);
        if (uploadingNum.current <= 0) {
            if (isFunction(onStateChange)) {
                onStateChange(false);
            }
        }
    };

    const handleStartUploadFiles = (upFiles: FileList | string[]) => {
        uploadingNum.current += size(upFiles);
        if (isFunction(onStateChange)) {
            onStateChange(true);
        }
    };

    const checkUpFiles = (upFiles: FileList) => {
        // 校验文件数量
        if (!checkFilesNum(upFiles)) {
            return false;
        }

        // 校验文件类型
        if (
            some(upFiles, (upFile) => {
                const filename = upFile.name;
                const [, extName] = parseFileName(filename);
                return (
                    accept &&
                    !includes(split(accept.toLowerCase(), /\,/), `.${extName.toLowerCase()}`)
                );
            })
        ) {
            const upSupportFileTip = isFunction(unSupportFileTypeTip)
                ? unSupportFileTypeTip(upFiles!)
                : unSupportFileTypeTip;
            if (upSupportFileTip) {
                Message.add({
                    messageType: 'toast',
                    message: upSupportFileTip,
                    type: 'warning'
                });
            }
            return false;
        }

        const maxSize = getMaxFileSize ? getMaxFileSize(upFiles) : maxFileSize;
        // 校验文件大小
        if (isNumber(maxSize)) {
            const invalid = some(upFiles, (upFile) => {
                if (upFile.size > maxSize) {
                    const sizeTip = isFunction(maxSizeTip)
                        ? maxSizeTip(maxSize! / 1024 / 1024)
                        : maxSizeTip;
                    if (sizeTip) {
                        Message.add({
                            messageType: 'toast',
                            message: sizeTip,
                            type: 'warning'
                        });
                        return true;
                    }
                    return false;
                }
            });
            if (invalid) {
                return false;
            }
        }

        if (isNumber(minFileSize)) {
            const invalid = some(upFiles, (upFile) => {
                if (upFile.size <= minFileSize) {
                    const sizeTip = isFunction(minSizeTip)
                        ? minSizeTip(minFileSize! / 1024 / 1024)
                        : minSizeTip;
                    if (sizeTip) {
                        Message.add({
                            messageType: 'toast',
                            message: sizeTip,
                            type: 'warning'
                        });
                        return true;
                    }
                    return false;
                }
            });
            if (invalid) {
                return false;
            }
        }

        return true;
    };

    const handleBeforeUpload = (upFiles: FileList) => {
        // 单文件上传，自动清空已上传数据（ocr)
        if (maxFileCount === UPLOAD_FILE_COUNT.SINGLE) {
            uploadedFields.current = [];
        }

        if (!checkUpFiles(upFiles)) {
            return false;
        }

        if (isFunction(onBeforeUpload) && onBeforeUpload(upFiles) === false) {
            return false;
        }

        handleStartUploadFiles(upFiles);

        // 获取oss token
        if (uploadPolicyUrl) {
            if (data) {
                return true;
            }
            const policyData = onGetUploadPolicyData ? onGetUploadPolicyData(upFiles) : {};
            set(policyData, 'fileCount', size(upFiles));
            const tokenRest = isFunction(onGetUploadPolicyResult)
                ? onGetUploadPolicyResult(upFiles)
                : {};
            return post(uploadPolicyUrl, policyData)
                .then((response) => {
                    setActionUrl(get(response, 'data.url'));
                    const token = get(response, 'data.token');
                    tokenList.current = concat(tokenList.current, get(response, 'data.tokenList'));
                    if (token) {
                        return {
                            token,
                            ...tokenRest
                        };
                    }
                    return tokenRest;
                })
                .catch((error) => {
                    if (isFunction(onGetUploadPolicyError)) {
                        onGetUploadPolicyError(error);
                    }
                    throw error;
                });
        }
        // 获取token
        if (uploadTokenUrl) {
            const data = onGetUploadTokenData
                ? onGetUploadTokenData(upFiles)
                : {
                      fileNames: map(upFiles, (file) => file.name),
                      fileCount: size(upFiles)
                  };
            const tokenRest = isFunction(onGetUploadTokenResult)
                ? onGetUploadTokenResult(upFiles)
                : {};
            const cfg = isFunction(onGetUploadTokenError) ? { customHandleHttpCodes: [400] } : {};
            return post(uploadTokenUrl, data, cfg)
                .then((response) => {
                    setActionUrl(get(response, 'data.url'));
                    const token = get(response, 'data.token');
                    tokenList.current = concat(tokenList.current, get(response, 'data.tokenList'));
                    if (token) {
                        return {
                            token,
                            ...tokenRest
                        };
                    }
                    return tokenRest;
                })
                .catch((error) => {
                    if (isFunction(onGetUploadTokenError)) {
                        onGetUploadTokenError(error);
                    }
                    dispatchFiles({
                        type: 'removeFile',
                        payload: {
                            files: map(upFiles, (upFile) => upFile)
                        }
                    });
                    each(upFiles, () => handleEndUploadFile());
                    throw error;
                });
        }

        return true;
    };

    const handleUploadProgress = (e: UploadProgress, upFile: UploadFile) => {
        if (e.percent) {
            if (e.percent < 100) {
                // 上传中
                dispatchFiles({
                    type: 'change',
                    payload: {
                        files: [upFile],
                        value: { percent: e.percent }
                    }
                });
            }
        }
        if (onProgress) {
            onProgress(e, upFile);
        }
    };

    const handleUploadSuccess = (result: any, file: UploadFile) => {
        const resultFile = resultFileKey ? get(result, resultFileKey) : result;

        // 处理result异常情况
        if (isNil(resultFile)) {
            handleUploadError({}, file);
            return;
        }

        // 上传完成，100%的状态延时显示一下
        dispatchFiles({
            type: 'change',
            payload: {
                files: [file],
                value: { percent: 100 }
            }
        });
        setTimeout(() => {
            dispatchFiles({
                type: 'replace',
                payload: {
                    files: [file],
                    value: result.file
                }
            });

            if (multiple) {
                uploadedFields.current.push(resultFile);
            } else {
                uploadedFields.current = [resultFile];
            }

            if (onSuccess) {
                onSuccess(result, file);
            }

            handleEndUploadFile();
        }, 500);
    };

    const handleUploadError = (error: object, upFile: UploadFile) => {
        dispatchFiles({
            type: 'removeFile',
            payload: {
                files: [upFile]
            }
        });

        if (uploadFailTip) {
            Message.add({
                messageType: 'toast',
                type: 'warning',
                message: uploadFailTip
            });
        }
        if (onError) {
            onError(error, upFile);
        }

        handleEndUploadFile();
    };

    const handleFormDataCreate = (data: object, file: UploadFile) => {
        let token;
        if (tokenList.current) {
            token = tokenList.current.shift();
        }
        const reqData = assign({}, data);
        if (token) {
            assign(reqData, { token });
        }
        const formData = new FormData();
        forEach(reqData, (v, k) => {
            formData.append(k, v);
        });
        formData.append('file', file);
        return formData;
    };

    const setActionUrl = (url: string) => {
        if (isString(url)) {
            actionUrl.current = url;
        }
    };

    const renderFileList = () => {
        if (!isEmpty(files) && isFunction(defaultFileRender) && isFunction(newUploadFileRender)) {
            const content = map(listItems, (file: any, i: number) => {
                if (file.type === 'empty') {
                    return <div className="upload-item-empty" key={file._id || i} />;
                }
                return (
                    <div className="upload-item-wrapper" key={file._id || i}>
                        {isUploadFile(file)
                            ? newUploadFileRender(file, handleNewUploadRemove)
                            : defaultFileRender(file, handleDefaultUploadRemove)}
                    </div>
                );
            });
            if (allowSort) {
                return (
                    <ReactSortable
                        disabled={!allowSort}
                        className={classNames('upload-list-wrapper', 'allow-sort')}
                        list={listItems}
                        setList={(newState) => {
                            const oldIds = map(files, (file) => file._id);
                            const newFiles = filter(newState, (item) => item.type !== 'empty');
                            dispatchFiles({
                                type: 'reset',
                                payload: {
                                    files: newFiles
                                }
                            });
                            const newIds = map(newFiles, (file) => file._id);
                            /*
                                https://github.com/SortableJS/react-sortablejs?tab=readme-ov-file#nesting
                                react-sortablejs的setList方法在初始化、排序开始和结束都会执行
                                文件顺序实际发生变化时再触发回调
                             */
                            if (!isEqual(oldIds, newIds)) {
                                fileIdsRef.current = newIds;
                                handUploadChange();
                            }
                        }}
                        delay={0.1}
                        animation={200}
                        /*
                            TODO Cytus
                            forceFallback设置true时：firefox浏览器拖拽后会触发点击事件FXD-66888
                            forceFallback设置false时，safari浏览器拖拽时会多选中其他文本FXD-67066
                         */
                        forceFallback={!isFireFoxUA()}
                        fallbackTolerance={3}
                        scrollSensitivity={90}
                        scrollSpeed={20}
                        ghostClass="upload-item-ghost"
                        draggable=".upload-item-wrapper"
                    >
                        {content}
                    </ReactSortable>
                );
            } else {
                return <div className="upload-list-wrapper">{content}</div>;
            }
        }
    };

    const handleStartUpload = (file: File) => {
        dispatchFiles({
            type: multiple ? 'add' : 'reset',
            payload: {
                files: [
                    {
                        upFile: file,
                        percent: 0,
                        finish: false,
                        _id: null
                    }
                ]
            }
        });
    };

    return (
        <>
            {editable && (
                <BaseUpload
                    className={className}
                    style={style}
                    accept={accept}
                    headers={headers}
                    data={data}
                    multiple={multiple}
                    action={() => Promise.resolve(actionUrl.current)}
                    disabled={disabled}
                    withCredentials={withCredentials}
                    onBeforeUpload={handleBeforeUpload}
                    onProgress={handleUploadProgress}
                    onSuccess={handleUploadSuccess}
                    onError={handleUploadError}
                    ref={uploadRef}
                    enableDrag={enableDrag}
                    onDrop={onDrop}
                    onDragEnter={onDragEnter}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    text={text}
                    onFormDataCreate={handleFormDataCreate}
                    beforeUpload={beforeUpload}
                    onStartUpload={handleStartUpload}
                >
                    {children}
                </BaseUpload>
            )}
            {renderFileList()}
        </>
    );
});

export default Upload;
